import { Fragment } from 'react';
import { useS } from '../store/game';
import { Tag } from './ui';
import { TPL } from '../content/issues';
import { ISS } from '../content/states';
import { GROUPS, AXN } from '../content/axes';
import { PA, PN, posOf, wOf, score } from '../engine/rules';
import { de, sg } from '../engine/util';
import { vrOf, vrLabel } from '../engine/court';
import { SYNERGY } from '../content/issues_more';

/** Zielkonflikte und Synergien eines Gesetzes als Tags. iss = Problem des Gesetzes. */
export function SideTags({ fx, iss }: { fx: any; iss?: string | null }) {
  const S = useS();
  const side = fx?.side || {};
  const keys = Object.keys(side).filter((k) => ISS[k] && Math.abs(side[k]) >= .5).sort((a, b) => side[b] - side[a]);
  const has = (i: string) => S.laws.some((x: any) => x.iss === i && ['kraft', 'done'].includes(x.stage) && !x.rep && !x.bestand);
  const syn = iss ? SYNERGY.filter((sy) => !(S.syn || []).includes(sy.a + '+' + sy.b) && ((sy.a === iss && has(sy.b)) || (sy.b === iss && has(sy.a)))) : [];
  if (!keys.length && !syn.length) return null;
  return <div className="tags">
    {keys.map((k) => <Tag key={k} good={side[k] < 0}>{ISS[k].n} {side[k] > 0 ? 'verschärft' : 'entschärft'} {side[k] > 0 ? '+' : '−'}{Math.round(Math.abs(side[k]))}</Tag>)}
    {syn.map((sy) => <Tag key={sy.a + sy.b} good>Synergie mit {ISS[sy.a === iss ? sy.b : sy.a].n}</Tag>)}
  </div>;
}

const LEVEL = ['Klar dagegen', 'Eher dagegen', 'Neutral', 'Eher dafür', 'Klar dafür'];
const lvl = (v: number) => LEVEL[Math.max(0, Math.min(4, Math.round(v) + 2))];

/** Inhalt eines Gesetzes: Stellschrauben, Kosten, Wirkung, und wie gut es zu einer Partei passt (Punkt für Punkt). */
export function LawDetails({ L, viewer }: { L: any; viewer: string }) {
  const S = useS();
  const T = L.tpl != null && L.iss && ISS[L.iss] ? TPL(L.iss, L.tpl) : null, fx = L.fx || {}, p = L.p || {};
  const fit = score(viewer, fx.pos || {}), my = posOf(viewer), w = wOf(viewer);
  const axes = Object.keys(fx.pos || {}).map((a) => ({ a, v: fx.pos[a] as number, y: my[a] ?? 0, heart: (w[a] ?? 1) >= 2 }))
    .map((x) => ({ ...x, d: Math.abs(x.v - x.y) })).sort((a, b) => b.d - a.d);
  const gtags = Object.keys(GROUPS).filter((g) => Math.abs((fx.g || {})[g] || 0) >= .5);
  const vr = T ? vrOf(T, p) : 0;
  const tone = fit >= .75 ? 'up' : fit < .45 ? 'down' : 'warn';
  return (
    <div className="lawdet">
      <div className="ld-fit">
        <span className="label">Passt zu {viewer === 'YOU' ? S.name : PN(viewer)}</span>
        <div className="ld-meter"><i className={tone} style={{ width: `${Math.round(fit * 100)}%` }} /></div>
        <b className={'num ' + tone}>{Math.round(fit * 100)} %</b>
        <span className="sub">{fit >= .75 ? 'Entspricht weitgehend eurem Programm.' : fit < .45 ? 'Widerspricht eurem Programm deutlich.' : 'Teils, teils.'}</span>
      </div>
      {T && !T.custom && T.ps?.length > 0 && <div className="ld-sec"><span className="label">Was vorgeschlagen ist{L.r ? ` · ${L.r}` : ''}</span>
        <div className="kv">{T.ps.map((pa: any) => <Fragment key={pa.id}><span>{pa.l}</span><b className="num">{pa.k === 'r' ? `${de(p[pa.id], pa.st < 1 ? 1 : 0)} ${pa.u}` : pa.k === 'c' ? pa.o[p[pa.id]] : p[pa.id] ? 'Ja' : 'Nein'}</b></Fragment>)}</div>
        {T.d && <span className="sub">{T.d}</span>}</div>}
      <div className="ld-sec"><span className="label">Kosten und Wirkung</span>
        <div className="tags">
          <Tag good={(fx.b || 0) >= 0}>Haushalt {sg(fx.b || 0)} Mrd €/Jahr</Tag>
          {Math.abs(fx.gr || 0) >= .005 && <Tag good={fx.gr > 0}>Wachstum {sg(fx.gr, 2)} Pkt</Tag>}
          {Math.abs(fx.al || 0) >= .005 && <Tag good={fx.al < 0}>Arbeitslosigkeit {sg(fx.al, 2)} Pkt</Tag>}
          {fx.sev ? <Tag good>Problemdruck −{Math.round(fx.sev)}</Tag> : null}
          {gtags.map((g) => <Tag key={g} good={fx.g[g] > 0}>{GROUPS[g]} {sg(fx.g[g], 0)}</Tag>)}
          {fx.gg && <Tag good={false}>Grundgesetzänderung, braucht ⅔</Tag>}
          {vr >= .05 && <Tag good={false}>Verfassungsrisiko: {vrLabel(vr)}</Tag>}
        </div>
      </div>
      {(fx.side || L.iss) && <SideTags fx={fx} iss={L.iss} />}
      {(L.luck || L.surprise) && <div className="ld-sec"><span className="label">Nach Inkrafttreten</span>
        {L.luck && <span className={'sub ' + (L.luck < .9 ? 'down' : L.luck > 1.1 ? 'up' : '')}>Umsetzung: {Math.round(L.luck * 100)} % der geplanten Wirkung</span>}
        {L.surprise && <span className="sub">{L.surprise}</span>}</div>}
      {axes.length > 0 && <div className="ld-sec"><span className="label">Punkt für Punkt</span>
        <div className="ld-axes">{axes.map((x) => (
          <div key={x.a} className={'ld-ax d' + Math.min(2, Math.round(x.d))}>
            <span>{AXN[x.a]}{x.heart ? ' ♥' : ''}</span>
            <span className="sub">Gesetz: {lvl(x.v)} · {viewer === 'YOU' ? 'ihr' : PA(viewer)}: {lvl(x.y)}</span>
            <b>{x.d < .5 ? '✓' : x.d < 1.5 ? '≈' : '✗'}</b>
          </div>))}</div>
      </div>}
    </div>
  );
}
